import { useEffect, useState, useCallback } from "react";
import { Fingerprint, Trash2, Plus, Loader2, ShieldCheck, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { frCA } from "date-fns/locale";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { canUseBiometric } from "@/lib/pwa";
import { isBiometricAvailable, enrollBiometric } from "@/lib/webauthn";
import { SessionsPanel } from "@/components/SessionsPanel";
import { LexiquePanel } from "@/components/LexiquePanel";
import { CREATE_CLINICAL, REPORT_WRITE } from "@/lib/rbac";

const CATEGORIES = [
  ["report_signed", "Rapport de pathologie signé", "Un rapport de pathologie vous concernant est signé et disponible"],
  ["urgent_case_assigned", "Cas urgent assigné", "Un cas prioritaire vous est attribué"],
  ["ai_analysis_complete", "Analyse IA terminée", "Les résultats d'une analyse IA sont prêts"],
  ["garde_reminder", "Rappel de garde", "Rappel avant le début de votre garde"],
  ["mention", "Mentions", "Vous êtes mentionné dans un dossier"],
  ["system", "Système", "Messages et alertes système"],
];

function NotifPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/push/preferences").then(({ data }) => setPrefs(data)).catch(() => {});
  }, []);

  const save = async (next) => {
    setPrefs(next);
    setSaving(true);
    try { await api.put("/push/preferences", next); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!prefs) return <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  const setCat = (key, val) => save({ ...prefs, categories: { ...prefs.categories, [key]: val } });
  const setQuiet = (patch) => save({ ...prefs, quiet_hours: { ...prefs.quiet_hours, ...patch } });

  return (
    <div className="space-y-5" data-testid="notif-prefs">
      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {CATEGORIES.map(([key, label, desc]) => (
          <div key={key} className="flex items-center justify-between px-4 py-3.5">
            <div className="pr-4">
              <div className="text-sm font-medium text-slate-800">{label}</div>
              <div className="text-xs text-slate-500">{desc}</div>
            </div>
            <Switch checked={!!prefs.categories?.[key]} onCheckedChange={(v) => setCat(key, v)}
                    data-testid={`pref-${key}`} />
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-400" />
            <div>
              <div className="text-sm font-medium text-slate-800">Heures silencieuses</div>
              <div className="text-xs text-slate-500">Aucune notification push (sauf urgences) durant cette plage</div>
            </div>
          </div>
          <Switch checked={!!prefs.quiet_hours?.enabled} onCheckedChange={(v) => setQuiet({ enabled: v })}
                  data-testid="pref-quiet-enabled" />
        </div>
        {prefs.quiet_hours?.enabled && (
          <div className="flex items-center gap-3 mt-4 pl-6">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">De</span>
              <Input type="time" value={prefs.quiet_hours.start || "22:00"} className="w-28"
                     onChange={(e) => setQuiet({ start: e.target.value })} data-testid="pref-quiet-start" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">à</span>
              <Input type="time" value={prefs.quiet_hours.end || "07:00"} className="w-28"
                     onChange={(e) => setQuiet({ end: e.target.value })} data-testid="pref-quiet-end" />
            </div>
          </div>
        )}
      </div>
      <div className="text-[11px] text-slate-400">{saving ? "Enregistrement…" : "Modifications enregistrées automatiquement"}</div>
    </div>
  );
}

function BiometricSettings() {
  const { user } = useAuth();
  const [available, setAvailable] = useState(null);
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState(false);
  const allowed = canUseBiometric(user?.role);

  const load = useCallback(() => {
    api.get("/webauthn/credentials").then(({ data }) => setCreds(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!allowed) return;
    isBiometricAvailable().then(setAvailable);
    load();
  }, [allowed, load]);

  const add = async () => {
    setBusy(true);
    try {
      const name = /iPhone|iPad/.test(navigator.userAgent) ? "iPhone/iPad"
        : /Android/.test(navigator.userAgent) ? "Appareil Android"
        : (navigator.platform || "Cet appareil");
      await enrollBiometric(name);
      toast.success("Appareil biométrique enregistré");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Enregistrement annulé");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/webauthn/credentials/${id}`); toast.success("Appareil supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!allowed) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="bio-unavailable-role">
      La biométrie n'est pas disponible pour votre rôle.
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="biometric-settings">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
            <Fingerprint className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Déverrouillage rapide (Face ID / empreinte)</div>
            <p className="text-xs text-slate-500 mt-1 max-w-lg leading-relaxed">
              Accédez à l'application sans saisir votre mot de passe. La biométrie remplace la
              <b className="text-slate-600"> connexion</b>, mais <b className="text-slate-600">PAS la signature des rapports de pathologie</b> (TOTP toujours requis — Loi 25).
            </p>
          </div>
        </div>
        {available === false && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Aucun authentificateur biométrique détecté sur cet appareil.
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {creds.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="bio-empty">
            Aucun appareil enregistré
          </div>
        ) : creds.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-4 py-3" data-testid={`bio-cred-${c.id}`}>
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-4 w-4 text-teal-600" />
              <div>
                <div className="text-sm font-medium text-slate-800">{c.device_name || "Appareil"}</div>
                <div className="text-[11px] text-slate-400">
                  Enregistré le {c.created_at ? format(parseISO(c.created_at), "d MMM yyyy", { locale: frCA }) : "-"}
                </div>
              </div>
            </div>
            <button onClick={() => remove(c.id)} data-testid={`bio-remove-${c.id}`}
                    className="p-2 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <Button onClick={add} disabled={busy || available === false} data-testid="bio-add-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
        Ajouter cet appareil
      </Button>
    </div>
  );
}

export default function Profile() {
  const { t } = useLang();
  const { user } = useAuth();
  const canDictate = [...CREATE_CLINICAL, ...REPORT_WRITE].includes(user?.role);
  return (
    <div data-testid="profile-page">
      <PageHeader title={t("nav_profile")} subtitle="Notifications et sécurité biométrique" />
      <Tabs defaultValue="notifications" className="w-full">
        <TabsList className="mb-5">
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="biometrie" data-testid="tab-biometrie">Biométrie</TabsTrigger>
          <TabsTrigger value="appareils" data-testid="tab-appareils">Appareils connectés</TabsTrigger>
          {canDictate && <TabsTrigger value="lexique" data-testid="tab-lexique">Lexique de dictée</TabsTrigger>}
        </TabsList>
        <TabsContent value="notifications"><NotifPrefs /></TabsContent>
        <TabsContent value="biometrie"><BiometricSettings /></TabsContent>
        <TabsContent value="appareils"><SessionsPanel /></TabsContent>
        {canDictate && <TabsContent value="lexique"><LexiquePanel /></TabsContent>}
      </Tabs>
    </div>
  );
}
